import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, secureFileUrl } from "../lib/api";
import { AuthImage } from "../components/AuthImage";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  FileCheck2, Upload, ArrowLeft, Building2, Percent, ReceiptText, StickyNote, PenTool, Trash2,
  Bold, Save, Download, Send, Loader2, GripVertical, Plus, Minus, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon, MapPin, Users, CreditCard, X, Check,
} from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);

const PALETTE_PREV = [
  { tipo: "fatturazione", label: "Dati fatturazione", icon: Building2 },
  { tipo: "iva", label: "IVA", icon: Percent },
  { tipo: "ritenuta", label: "Ritenuta d'acconto", icon: ReceiptText },
  { tipo: "spesa", label: "Tipo spesa", icon: ReceiptText },
  { tipo: "note", label: "Note", icon: StickyNote },
  { tipo: "luogodata", label: "Luogo e data", icon: MapPin },
  { tipo: "firma", label: "Firma / Logo", icon: PenTool },
  { tipo: "spunta", label: "Spunta ✓", icon: Check },
  { tipo: "croce", label: "Croce ✗", icon: X },
];
const PALETTE_SIN = [
  { tipo: "dati_condominio", label: "Dati condominio", icon: Building2 },
  { tipo: "danneggiati", label: "Dati danneggiato/i", icon: Users },
  { tipo: "iban", label: "IBAN condominio", icon: CreditCard },
  { tipo: "note", label: "Note", icon: StickyNote },
  { tipo: "luogodata", label: "Luogo e data", icon: MapPin },
  { tipo: "firma", label: "Firma / Logo", icon: PenTool },
  { tipo: "spunta", label: "Spunta ✓", icon: Check },
  { tipo: "croce", label: "Croce ✗", icon: X },
];
const LABELS = Object.fromEntries([...PALETTE_PREV, ...PALETTE_SIN].map((p) => [p.tipo, p.label]));

const oggiIt = () => new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const computeTesto = (f) => {
  if (f.tipo === "iva") return f.iva_aliquota === "22" ? "IVA 22%" : "IVA 10%";
  if (f.tipo === "ritenuta") return f.ritenuta === "SI" ? "Si Ritenuta Acconto" : "No Ritenuta Acconto";
  if (f.tipo === "spesa") return f.spesa === "no" ? "Spesa non fisc. detraib. (Bon. Ordinario)" : "Spesa fiscalmente detraibile (Bonif. parlante)";
  return f.testo || "";
};
const FREE_TEXT = ["note", "fatturazione", "luogodata", "dati_condominio", "iban", "danneggiati"];

export default function ConfermaPreventivo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [loading, setLoading] = useState(!!id);
  const [conf, setConf] = useState(null);      // {id, numero, ...}
  const [pages, setPages] = useState([]);
  const [fields, setFields] = useState([]);
  const [selId, setSelId] = useState(null);
  const [condomini, setCondomini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [condominioId, setCondominioId] = useState("");
  const [fornitoreEmail, setFornitoreEmail] = useState("");
  const [fornitoreId, setFornitoreId] = useState("");
  const [fornitoreNome, setFornitoreNome] = useState("");
  const [fornitoreSearch, setFornitoreSearch] = useState("");
  const [firmaFileId, setFirmaFileId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sourceFileId, setSourceFileId] = useState("");
  // Iter123k256 · elenco PDF uniti (per visualizzare lo stato dopo l'upload multi)
  const [mergedParti, setMergedParti] = useState([]);
  const [preventivoId, setPreventivoId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [categorie, setCategorie] = useState([]);
  // Iter122aq · oggetto/descrizione della prestazione (finisce nella descrizione della manutenzione)
  const [titolo, setTitolo] = useState("");
  const [contesto, setContesto] = useState("preventivo");
  const [sinistroId, setSinistroId] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [sinistro, setSinistro] = useState(null);
  // Iter122t · sottoscrizione editor per extra capitolato Appalti
  const [appaltoId, setAppaltoId] = useState("");
  const [extraId, setExtraId] = useState("");
  const [destinatari, setDestinatari] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const [pw, setPw] = useState(760);
  const [offerteEsistenti, setOfferteEsistenti] = useState([]);
  const [offertaSelezionata, setOffertaSelezionata] = useState("");
  const [loadingOfferta, setLoadingOfferta] = useState(false);
  const pageRefs = useRef({});
  const drag = useRef(null);
  const isSin = contesto === "sinistro";
  const isExtra = contesto === "appalto_extra";
  const palette = isSin ? PALETTE_SIN : PALETTE_PREV;
  const soggettiSin = (isSin && sinistro) ? [
    { key: "compagnia", label: "Compagnia", nome: sinistro.compagnia_nome, email: (sinistro.compagnia_email || "").trim() },
    { key: "compagnia_pec", label: "Compagnia (PEC)", nome: sinistro.compagnia_nome, email: (sinistro.compagnia_pec || "").trim() },
    { key: "agenzia", label: "Agenzia", nome: sinistro.agenzia_nome, email: (sinistro.agenzia_email || "").trim() },
    { key: "agenzia_pec", label: "Agenzia (PEC)", nome: sinistro.agenzia_nome, email: (sinistro.agenzia_pec || "").trim() },
    { key: "perito", label: "Perito", nome: sinistro.perito_nome, email: (sinistro.perito_email || "").trim() },
    { key: "liquidatore", label: "Liquidatore", nome: sinistro.liquidatore_nome, email: (sinistro.liquidatore_email || "").trim() },
  ].filter((s) => s.email) : [];
  const toggleDest = (email) => setDestinatari((p) => p.includes(email) ? p.filter((e) => e !== email) : [...p, email]);

  useEffect(() => {
    const measure = () => { const el = pageRefs.current[0]; if (el) setPw(el.clientWidth); };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [pages.length]);

  useEffect(() => {
    api.get("/condomini?limit=1000").then((r) => setCondomini(r.data.condomini || r.data || [])).catch(() => {});
    api.get("/fornitori?limit=1000").then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
    api.get("/conferme/firma").then((r) => setFirmaFileId(r.data.file_id || "")).catch(() => {});
    api.get("/meta").then((r) => setCategorie(r.data.categorie || [])).catch(() => {});
    api.get("/preventivi/con-offerta").then((r) => setOfferteEsistenti(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/conferme/${id}`).then((r) => {
      const d = r.data;
      setConf(d); setPages(d.pages || []); setFields(d.campi || []);
      setCondominioId(d.condominio_id || ""); setFornitoreEmail(d.fornitore_email || "");
      setFornitoreId(d.fornitore_id || ""); setFornitoreNome(d.fornitore_nome || "");
      setSourceFileId(d.source_file_id || "");
      setContesto(d.contesto || "preventivo"); setSinistroId(d.sinistro_id || ""); setTicketId(d.ticket_id || ""); setCategoria(d.categoria || "");
      setAppaltoId(d.appalto_id || ""); setExtraId(d.extra_id || "");
      if (d.sinistro_id) api.get(`/sinistri/${d.sinistro_id}`).then((s) => setSinistro(s.data)).catch(() => {});
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (id) return;
    const cid = search.get("condominio_id"); const fe = search.get("fornitore_email");
    const pid = search.get("preventivo_id"); const sf = search.get("source_file_id");
    const ctx = search.get("contesto") || "preventivo"; const sid = search.get("sinistro_id");
    const tkid = search.get("ticket_id");
    // Iter122t · parametri per sottoscrizione extra capitolato Appalti
    const aid = search.get("appalto_id"); const eid = search.get("extra_id");
    setContesto(ctx);
    if (aid) setAppaltoId(aid);
    if (eid) setExtraId(eid);
    if (cid) setCondominioId(cid);
    if (fe) setFornitoreEmail(fe);
    if (pid) setPreventivoId(pid);
    if (tkid) setTicketId(tkid);
    if (sid) { setSinistroId(sid); api.get(`/sinistri/${sid}`).then((s) => setSinistro(s.data)).catch(() => {}); }
    if (sf) {
      setSourceFileId(sf);
      api.get(`/conferme/pdf-pages/${sf}`).then((r) => setPages(r.data.pages || [])).catch(() => {});
    }
  }, [id, search]);

  // Iter123k160 · risolve fornitore_id/fornitore_nome dall'email quando l'email cambia
  // o quando l'anagrafica fornitori viene caricata. Copre: precompilato da URL, ricarica conferma
  // esistente (con id già valorizzato), richiamo offerta esistente, digitazione manuale in cui l'email
  // matcha un fornitore in anagrafica. Se l'email non matcha nessun fornitore, id/nome restano vuoti.
  useEffect(() => {
    if (!fornitoreEmail || !fornitori.length) return;
    const email = fornitoreEmail.trim().toLowerCase();
    const forn = fornitori.find((f) => (f.email || "").toLowerCase() === email);
    if (forn && (forn.id !== fornitoreId || forn.ragione_sociale !== fornitoreNome)) {
      setFornitoreId(forn.id);
      setFornitoreNome(forn.ragione_sociale || "");
    }
  }, [fornitoreEmail, fornitori, fornitoreId, fornitoreNome]);

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/conferme/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPages(r.data.pages || []); setSourceFileId(r.data.file_id);
      setMergedParti([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploading(false); }
  };

  // Iter123k256 · Upload multi-PDF per "Sottoscrivi accordo di liquidazione":
  // più PDF vengono uniti nell'ordine di invio e aperti come documento unico.
  const uploadPdfMulti = async (fileList) => {
    const pdfs = Array.from(fileList || []).filter(
      (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name)
    );
    if (!pdfs.length) { toast.error("Nessun PDF valido selezionato"); return; }
    if (pdfs.length === 1) { return uploadPdf(pdfs[0]); }
    if (pdfs.length > 10) { toast.error("Massimo 10 PDF per unione"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      pdfs.forEach((f) => fd.append("files", f, f.name));
      const r = await api.post("/conferme/upload-pdf-multi", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPages(r.data.pages || []);
      setSourceFileId(r.data.file_id);
      setMergedParti(r.data.merged_from || []);
      toast.success(`${pdfs.length} PDF uniti in ${(r.data.pages || []).length} pagine`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Unione PDF non riuscita");
    } finally { setUploading(false); }
  };

  const richiamaOffertaEsistente = async (key) => {
    setOffertaSelezionata(key);
    if (!key) return;
    const item = offerteEsistenti.find((o) => `${o.preventivo_id}::${o.file_id}` === key);
    if (!item) return;
    setLoadingOfferta(true);
    try {
      const r = await api.get(`/conferme/pdf-pages/${item.file_id}`);
      setPages(r.data.pages || []);
      setSourceFileId(item.file_id);
      if (item.condominio_id) setCondominioId(item.condominio_id);
      if (item.fornitore_email) setFornitoreEmail(item.fornitore_email);
      if (item.categoria) setCategoria(item.categoria);
      setPreventivoId(item.preventivo_id);
      toast.success(`Offerta ${item.numero} richiamata: PDF caricato e dati precompilati.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile richiamare l'offerta");
      setOffertaSelezionata("");
    } finally { setLoadingOfferta(false); }
  };

  const uploadFirma = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/conferme/firma", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFirmaFileId(r.data.file_id);
      toast.success("Firma/logo salvata: sarà riutilizzabile per tutte le conferme.");
      return r.data.file_id;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
  };

  const createDraft = async () => {
    if (!sourceFileId) return toast.error("Carica prima il PDF del preventivo");
    setBusy(true);
    try {
      const r = await api.post("/conferme", { condominio_id: condominioId, source_file_id: sourceFileId, fornitore_email: fornitoreEmail, fornitore_id: fornitoreId, fornitore_nome: fornitoreNome, preventivo_id: preventivoId, contesto, sinistro_id: sinistroId, ticket_id: ticketId, appalto_id: appaltoId, extra_id: extraId, categoria, titolo: (titolo || "").trim(), campi: [] });
      navigate(`/conferma-preventivo/${r.data.id}`, { replace: true });
      setConf(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addField = async (tipo) => {
    // Iter121x · croce/spunta hanno dimensioni quadrate piccole di default
    const isSymbol = tipo === "croce" || tipo === "spunta";
    const base = {
      id: uid(), tipo, page: 0, x: 0.12, y: 0.15,
      w: isSymbol ? 0.04 : (tipo === "firma" ? 0.28 : 0.5),
      h: isSymbol ? 0.03 : 0.12,
      font_frac: 0.016, bold: false, align: 0,
    };
    if (tipo === "fatturazione") {
      let testo = "Dati fatturazione condominio";
      if (condominioId) { try { const r = await api.get(`/conferme/billing/${condominioId}`); testo = r.data.testo || testo; } catch {} }
      base.testo = testo;
    } else if (tipo === "iva") { base.iva_aliquota = "10"; }
    else if (tipo === "ritenuta") { base.ritenuta = "NO"; }
    else if (tipo === "spesa") { base.spesa = "si"; }
    else if (tipo === "note") { base.testo = "Note: "; }
    else if (tipo === "luogodata") { base.testo = `Legnano, ${oggiIt()}`; }
    else if (tipo === "dati_condominio") {
      let testo = "Dati condominio";
      if (condominioId) { try { const r = await api.get(`/conferme/billing/${condominioId}?scopo=sinistro`); testo = r.data.testo || testo; } catch {} }
      base.testo = testo;
    } else if (tipo === "iban") {
      let iban = "";
      if (condominioId) { try { const r = await api.get(`/conferme/billing/${condominioId}?scopo=sinistro`); iban = r.data.iban || ""; } catch {} }
      base.testo = `IBAN: ${iban}`;
    } else if (tipo === "danneggiati") {
      const dd = (sinistro?.danneggiati || []);
      base.testo = dd.length ? "Danneggiato/i:\n" + dd.map((d) => `- ${[d.nome, d.descrizione].filter(Boolean).join(" — ")}`).join("\n") : "Danneggiato/i: ";
    }
    else if (tipo === "firma") {
      if (!firmaFileId) { toast.error("Carica prima l'immagine della firma/logo (pulsante in alto)."); return; }
      base.image_file_id = firmaFileId;
    }
    setFields((p) => [...p, base]); setSelId(base.id);
  };

  const updateField = (fid, patch) => setFields((p) => p.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
  const removeField = (fid) => { setFields((p) => p.filter((f) => f.id !== fid)); if (selId === fid) setSelId(null); };

  const onPointerDown = (e, f) => {
    e.stopPropagation();
    setSelId(f.id);
    const rect = pageRefs.current[f.page]?.getBoundingClientRect();
    if (!rect) return;
    drag.current = { fid: f.id, offX: e.clientX - (rect.left + f.x * rect.width), offY: e.clientY - (rect.top + f.y * rect.height) };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };
  const onPointerMove = useCallback((e) => {
    const d = drag.current; if (!d) return;
    // Individua la pagina attualmente sotto il puntatore (per consentire lo spostamento tra pagine)
    let targetPage = null, targetRect = null;
    const refs = pageRefs.current || {};
    for (const key of Object.keys(refs)) {
      const el = refs[key]; if (!el) continue;
      const r = el.getBoundingClientRect();
      if (e.clientY >= r.top && e.clientY <= r.bottom) { targetPage = +key; targetRect = r; break; }
    }
    setFields((prev) => prev.map((f) => {
      if (f.id !== d.fid) return f;
      const page = targetPage != null ? targetPage : f.page;
      const rect = targetRect || pageRefs.current[f.page]?.getBoundingClientRect();
      if (!rect) return f;
      let x = (e.clientX - d.offX - rect.left) / rect.width;
      let y = (e.clientY - d.offY - rect.top) / rect.height;
      return { ...f, page, x: Math.max(0, Math.min(0.98, x)), y: Math.max(0, Math.min(0.98, y)) };
    }));
  }, []);
  const onPointerUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  const payloadCampi = () => fields.map((f) => ({ ...f, testo: computeTesto(f) }));

  const saveDraft = async (silent) => {
    if (!conf) return;
    const r = await api.put(`/conferme/${conf.id}`, { condominio_id: condominioId, source_file_id: sourceFileId, fornitore_email: fornitoreEmail, fornitore_id: fornitoreId, fornitore_nome: fornitoreNome, contesto, sinistro_id: sinistroId, ticket_id: ticketId, preventivo_id: preventivoId, appalto_id: appaltoId, extra_id: extraId, categoria, campi: payloadCampi() });
    setConf((c) => ({ ...c, ...r.data }));
    if (!silent) toast.success("Bozza salvata");
  };

  const genera = async () => {
    // Iter123k192 · Pre-apro una tab vuota con il GESTO utente, poi redirect: evita blocco
    // popup del browser (prima la window.open avveniva DOPO 3 await → "popup blocked").
    const w = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      await saveDraft(true);
      const r = await api.post(`/conferme/${conf.id}/genera`);
      const url = await secureFileUrl(`/files/${r.data.signed_file_id}`);
      if (w && !w.closed) {
        w.location.replace(url);
      } else {
        const a = document.createElement("a");
        a.href = url; a.target = "_blank"; a.rel = "noreferrer";
        document.body.appendChild(a); a.click(); a.remove();
      }
      toast.success("PDF generato");
    } catch (e) {
      if (w && !w.closed) w.close();
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };

  const invia = async () => {
    if (isSin) {
      const lista = [...destinatari];
      const m = manualEmail.trim();
      if (m) lista.push(m);
      const uniq = [...new Set(lista.filter(Boolean))];
      if (!uniq.length) return toast.error("Seleziona almeno un destinatario (Perito, Liquidatore, Compagnia o Agenzia)");
      if (!window.confirm(`Inviare l'accordo di liquidazione a:\n${uniq.join("\n")}?`)) return;
      setBusy(true);
      try {
        await saveDraft(true);
        await api.post(`/conferme/${conf.id}/invia`, { emails: uniq });
        toast.success(`Accordo inviato a ${uniq.length} destinatario/i`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    if (!fornitoreEmail.trim()) return toast.error("Inserisci l'email del fornitore");
    if (!window.confirm(`Inviare la conferma d'ordine a ${fornitoreEmail}?`)) return;
    setBusy(true);
    try {
      await saveDraft(true);
      await api.post(`/conferme/${conf.id}/invia`, { email: fornitoreEmail });
      toast.success(`Conferma inviata a ${fornitoreEmail}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>;

  const sel = fields.find((f) => f.id === selId);

  // ---- START SCREEN (no draft yet) ----
  if (!conf) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(isExtra ? "/appalti" : (isSin ? "/sinistri" : "/preventivi"))} className="flex items-center gap-2 text-slate-500 hover:text-brand mb-4"><ArrowLeft className="w-4 h-4" /> {isExtra ? "Appalti" : (isSin ? "Sinistri" : "Preventivi")}</button>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-3 mb-2"><FileCheck2 className="w-8 h-8 text-brand" /> {isExtra ? "Sottoscrivi offerta extra capitolato" : (isSin ? "Sottoscrivi accordo" : "Conferma preventivo")}</h1>
        <p className="text-slate-500 mb-6">{isExtra ? "Carica il PDF dell'offerta extra capitolato ricevuta dall'appaltatore, poi aggiungi e posiziona i campi (dati, timbri, firma) per sottoscriverla. Il PDF firmato sarà allegato automaticamente alla voce Extra dell'appalto." : (isSin ? "Carica il PDF dell'accordo di liquidazione ricevuto dal perito/liquidatore, verifica il condominio, poi aggiungi e posiziona i campi (dati condominio, danneggiati, IBAN, firma)." : "Carica il PDF del preventivo (anche esterno all'app), scegli il condominio e il fornitore, poi aggiungi e posiziona i campi.")}</p>
        <Card className="p-6 space-y-5">
          {!isSin && offerteEsistenti.length > 0 && (
            <div className="pb-4 border-b border-slate-200" data-testid="conf-richiamo-block">
              <Label className="font-bold flex items-center gap-2">
                <FileCheck2 className="w-4 h-4 text-brand" /> Richiama un'offerta già ricevuta o archiviata (opzionale)
              </Label>
              <select data-testid="conf-richiamo-offerta" value={offertaSelezionata}
                onChange={(e) => richiamaOffertaEsistente(e.target.value)} disabled={loadingOfferta}
                className="mt-2 w-full h-12 border-2 border-slate-300 rounded-lg px-3 bg-white disabled:opacity-60">
                <option value="">— Nessuna: caricherò io il PDF —</option>
                {offerteEsistenti.map((o) => {
                  const key = `${o.preventivo_id}::${o.file_id}`;
                  const orig = o.origine === "studio" ? "caricata dallo Studio" : "ricevuta dal fornitore";
                  const arc = o.archiviato ? " · archiviata" : "";
                  const forn = o.fornitore_nome || o.fornitore_email || "—";
                  return (
                    <option key={key} value={key}>
                      {o.numero} · {o.condominio_nome || "—"} · {forn} · {o.stato_display}{arc} · {orig} · {o.file_name}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {loadingOfferta ? "Caricamento offerta in corso…" : "Selezionando un'offerta esistente vengono precompilati PDF, condominio, fornitore e categoria."}
              </p>
            </div>
          )}
          <div>
            <Label className="font-bold">1. PDF {isSin ? "dell'accordo di liquidazione" : "del preventivo"}</Label>
            {isSin && (
              <p className="text-xs text-slate-500 mt-1">
                Puoi caricare <b>più PDF</b>: verranno uniti in sequenza nell'ordine di selezione (es. atto + informativa + IBAN).
              </p>
            )}
            <label
              data-testid="conf-upload-pdf"
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setPdfDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setPdfDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setPdfDragOver(false); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation(); setPdfDragOver(false);
                const files = Array.from(e.dataTransfer?.files || []).filter(
                  (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name)
                );
                if (!files.length) { toast.error("Trascina almeno un file PDF valido"); return; }
                if (isSin && files.length > 1) { uploadPdfMulti(files); }
                else { uploadPdf(files[0]); }
              }}
              className={`mt-2 flex items-center justify-center gap-2 h-28 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${pdfDragOver ? "border-[#C98AA0] bg-[#C98AA0]/10 text-[#8A2A47]" : "hover:border-[#C98AA0] text-slate-500"}`}
            >
              {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</>
                : sourceFileId ? <><FileCheck2 className="w-6 h-6 text-green-600" /> PDF caricato ({pages.length} pag.{mergedParti.length ? ` · ${mergedParti.length} file uniti` : ""}) — clicca o trascina {isSin ? "nuovi PDF" : "un nuovo PDF"} per sostituire</>
                : pdfDragOver ? <><Upload className="w-6 h-6" /> Rilascia {isSin ? "i PDF" : "il PDF"} qui</>
                : <><Upload className="w-6 h-6" /> Trascina {isSin ? "uno o più PDF" : "il PDF"} o clicca per selezionar{isSin ? "li" : "lo"}</>}
              <input type="file" accept="application/pdf,.pdf"
                multiple={isSin}
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  if (isSin && files.length > 1) uploadPdfMulti(files);
                  else uploadPdf(files[0]);
                  e.target.value = "";
                }} />
            </label>
            {mergedParti.length > 0 && (
              <ol data-testid="conf-merged-parti" className="mt-2 text-xs text-slate-600 list-decimal pl-5 space-y-0.5">
                {mergedParti.map((p, i) => (
                  <li key={i}>
                    <span className="font-mono text-slate-400">p.{p.page_start + 1}-{p.page_end + 1}</span>{" "}
                    <span className="truncate">{p.filename}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div>
            <Label className="font-bold">2. Condominio (per i dati {isSin ? "e l'IBAN" : "di fatturazione"})</Label>
            <select data-testid="conf-condominio" value={condominioId} onChange={(e) => setCondominioId(e.target.value)} className="mt-2 w-full h-12 border-2 border-slate-300 rounded-lg px-3 bg-white">
              <option value="">— Seleziona condominio —</option>
              {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          {!isSin && (
          <div>
            <Label className="font-bold">3. Email fornitore (destinatario)</Label>
            <div className="mt-2 relative">
              <Input
                data-testid="conf-fornitore-search"
                type="search"
                placeholder="Cerca fornitore per nome, email o categoria…"
                value={fornitoreSearch}
                onChange={(e) => setFornitoreSearch(e.target.value)}
                className="h-11 pl-3 pr-9"
                autoComplete="off"
              />
              {fornitoreSearch && (
                <button
                  type="button"
                  data-testid="conf-fornitore-search-clear"
                  onClick={() => setFornitoreSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Pulisci ricerca"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {(() => {
              const q = fornitoreSearch.trim().toLowerCase();
              const withMail = fornitori.filter((f) => f.email);
              const filtrati = q
                ? withMail.filter((f) => {
                    const cats = Array.isArray(f.categorie) ? f.categorie.join(" ") : (f.categoria || "");
                    const hay = `${f.ragione_sociale || ""} ${f.email || ""} ${cats}`.toLowerCase();
                    return hay.includes(q);
                  })
                : withMail;
              return (
                <>
                  <select data-testid="conf-fornitore" value={fornitoreEmail}
                    onChange={(e) => {
                      const email = e.target.value;
                      setFornitoreEmail(email);
                      const forn = fornitori.find((f) => f.email === email);
                      setFornitoreId(forn?.id || "");
                      setFornitoreNome(forn?.ragione_sociale || "");
                    }}
                    className="mt-2 w-full h-12 border-2 border-slate-300 rounded-lg px-3 bg-white">
                    <option value="">— Seleziona fornitore —</option>
                    {filtrati.map((f) => <option key={f.id} value={f.email}>{f.ragione_sociale} ({f.email})</option>)}
                  </select>
                  {q && (
                    <div data-testid="conf-fornitore-search-count" className="text-xs text-slate-500 mt-1">
                      {filtrati.length === 0 ? "Nessun fornitore corrisponde alla ricerca" : `${filtrati.length} risultat${filtrati.length === 1 ? "o" : "i"} su ${withMail.length}`}
                    </div>
                  )}
                </>
              );
            })()}
            <Input className="mt-2 h-11" placeholder="oppure inserisci un'email manualmente" value={fornitoreEmail}
              onChange={(e) => {
                const email = e.target.value;
                setFornitoreEmail(email);
                // Se l'utente digita manualmente, prova a rimatchare con anagrafica (case-insensitive)
                const forn = fornitori.find((f) => (f.email || "").toLowerCase() === email.toLowerCase());
                setFornitoreId(forn?.id || "");
                setFornitoreNome(forn?.ragione_sociale || "");
              }}
              data-testid="conf-fornitore-email" />
          </div>
          )}
          {!isSin && (
          <div>
            <Label className="font-bold">4. Categoria (come nelle manutenzioni)</Label>
            <select data-testid="conf-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className="mt-2 w-full h-12 border-2 border-slate-300 rounded-lg px-3 bg-white">
              <option value="">— Seleziona categoria —</option>
              {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          )}
          {!isSin && !isExtra && (
          <div>
            <Label className="font-bold">5. Oggetto / descrizione della prestazione</Label>
            <Input data-testid="conf-titolo" className="mt-2 h-12" value={titolo} onChange={(e) => setTitolo(e.target.value)}
              placeholder="es. Sostituzione caldaia condominiale e revisione impianto termico" />
            <p className="text-xs text-slate-500 mt-1">
              Questo testo diventerà la <b>descrizione della manutenzione</b> generata dalla conferma. Se lasciato vuoto viene usato un placeholder generico.
            </p>
          </div>
          )}
          <Button data-testid="conf-create-draft" disabled={!sourceFileId || busy} onClick={createDraft} className="h-12 w-full bg-brand hover:bg-brand-dark text-base font-bold">
            {busy ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <FileCheck2 className="w-5 h-5 mr-2" />} Apri l'editor
          </Button>
        </Card>
      </div>
    );
  }

  // ---- EDITOR ----
  return (
    <div className="p-3 sm:p-5 max-w-6xl mx-auto pb-24">
      <div className="lg:sticky lg:top-0 lg:z-30 bg-white/95 backdrop-blur-sm lg:pt-2 lg:-mt-2 lg:pb-1">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <button onClick={() => navigate(isSin ? "/sinistri" : "/preventivi")} className="flex items-center gap-2 text-slate-500 hover:text-brand"><ArrowLeft className="w-4 h-4" /> {isSin ? "Sinistri" : "Preventivi"}</button>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <span className="text-sm font-bold text-slate-500 mr-auto sm:mr-0">{conf.numero}</span>
          <Button data-testid="conf-save" variant="outline" size="sm" className="h-10 flex-1 sm:flex-none min-w-0" onClick={() => saveDraft(false)}><Save className="w-4 h-4 mr-1" /> Salva</Button>
          <Button data-testid="conf-genera" variant="outline" size="sm" className="h-10 flex-1 sm:flex-none min-w-0 text-brand border-brand/40" disabled={busy} onClick={genera}><Download className="w-4 h-4 mr-1" /> Genera</Button>
          <Button data-testid="conf-invia" size="sm" className="h-10 flex-1 sm:flex-none min-w-0 bg-brand hover:bg-brand-dark" disabled={busy} onClick={invia}><Send className="w-4 h-4 mr-1" /> Invia</Button>
        </div>
      </div>

      {/* Destinatari accordo (solo sinistri) */}
      {isSin && (
        <Card className="p-3 mb-3" data-testid="conf-destinatari">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-slate-500 flex items-center gap-1"><Send className="w-4 h-4" /> Invia accordo a:</span>
            {soggettiSin.length === 0 && <span className="text-sm text-slate-400">Nessuna email soggetto trovata nel sinistro. Usa il campo manuale.</span>}
            {soggettiSin.map((s) => (
              <label key={s.key} data-testid={`conf-dest-${s.key}`} className={`inline-flex items-center gap-2 px-3 h-9 rounded-lg border-2 cursor-pointer text-sm ${destinatari.includes(s.email) ? "border-brand bg-brand/5 text-brand font-semibold" : "border-slate-200 text-slate-600"}`}>
                <input type="checkbox" className="accent-brand" checked={destinatari.includes(s.email)} onChange={() => toggleDest(s.email)} />
                {s.label} {s.nome ? `(${s.nome})` : ""}
              </label>
            ))}
            <Input className="h-9 w-56" placeholder="oppure email manuale" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} data-testid="conf-dest-manual" />
          </div>
        </Card>
      )}

      {/* Palette */}
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-slate-500 mr-1">Aggiungi campo:</span>
          {palette.map((p) => (
            <Button key={p.tipo} data-testid={`conf-add-${p.tipo}`} variant="outline" size="sm" className="h-9" onClick={() => addField(p.tipo)}>
              <p.icon className="w-4 h-4 mr-1" /> {p.label}
            </Button>
          ))}
          <label className="ml-auto inline-flex items-center gap-1 text-sm text-brand cursor-pointer font-semibold" title="Carica/aggiorna firma o logo (riutilizzabile)">
            <ImageIcon className="w-4 h-4" /> {firmaFileId ? "Cambia firma/logo" : "Carica firma/logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFirma(e.target.files?.[0])} data-testid="conf-upload-firma" />
          </label>
        </div>
      </Card>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-4">
        {/* PDF pages with overlay fields — scroll interno: solo il documento scorre */}
        <div className="space-y-4 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:pr-2">
          {pages.map((pg) => (
            <div key={pg.page} className="relative mx-auto shadow-md border" style={{ maxWidth: 820 }}
              ref={(el) => (pageRefs.current[pg.page] = el)} onPointerDown={() => setSelId(null)}>
              <img src={pg.image} alt={`Pagina ${pg.page + 1}`} className="w-full block select-none pointer-events-none" draggable={false} />
              {fields.filter((f) => f.page === pg.page).map((f) => {
                const isSymbol = f.tipo === "croce" || f.tipo === "spunta";
                // Iter121x · sfondo giallo fluo sui campi testo per renderli più evidenti;
                // firma/croce/spunta non hanno sfondo (sono grafici o immagini)
                const bg = f.tipo === "firma" ? "" : (isSymbol ? "" : "bg-[#FFFF00]/70");
                return (
                <div key={f.id} data-testid={`conf-field-${f.tipo}`} onPointerDown={(e) => onPointerDown(e, f)}
                  className={`absolute cursor-move rounded ${selId === f.id ? "ring-2 ring-brand" : "ring-1 ring-slate-300/70"} ${bg}`}
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: isSymbol ? `${f.h * 100}%` : undefined }}>
                  <div className="absolute -top-3 -left-3 bg-brand text-white rounded-full w-5 h-5 flex items-center justify-center opacity-80"><GripVertical className="w-3 h-3" /></div>
                  {f.tipo === "firma" ? (
                    <AuthImage fileId={f.image_file_id} alt="firma" className="w-full pointer-events-none" style={{ height: "auto" }} draggable={false} />
                  ) : f.tipo === "croce" ? (
                    <svg viewBox="0 0 100 100" className="w-full h-full pointer-events-none" preserveAspectRatio="none">
                      <line x1="10" y1="10" x2="90" y2="90" stroke="#d91b1b" strokeWidth="12" strokeLinecap="round" />
                      <line x1="90" y1="10" x2="10" y2="90" stroke="#d91b1b" strokeWidth="12" strokeLinecap="round" />
                    </svg>
                  ) : f.tipo === "spunta" ? (
                    <svg viewBox="0 0 100 100" className="w-full h-full pointer-events-none" preserveAspectRatio="none">
                      <polyline points="15,55 42,85 88,20" stroke="#088c48" strokeWidth="12" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <div className="px-1 py-0.5 whitespace-pre-wrap break-words leading-tight text-black"
                      style={{ fontSize: `${f.font_frac * pw}px`, fontWeight: f.bold ? 700 : 400, textAlign: f.align === 1 ? "center" : f.align === 2 ? "right" : "left" }}>
                      {computeTesto(f)}
                    </div>
                  )}
                </div>
              );})}
            </div>
          ))}
        </div>

        {/* Properties panel — fisso a destra */}
        <div className="lg:sticky lg:top-4 h-fit lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto">
          <Card className="p-4">
            {!sel ? (
              <p className="text-sm text-slate-500">Seleziona un campo per modificarlo, oppure aggiungi un campo dalla palette. Trascina i campi per posizionarli sul PDF.</p>
            ) : (
              <div className="space-y-3" data-testid="conf-props">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-brand">{palette.find((p) => p.tipo === sel.tipo)?.label || LABELS[sel.tipo]}</span>
                  <Button variant="ghost" size="sm" className="h-8 text-red-600" onClick={() => removeField(sel.id)} data-testid="conf-field-delete"><Trash2 className="w-4 h-4" /></Button>
                </div>

                {sel.tipo === "iva" && (
                  <div className="flex gap-2">
                    <Button size="sm" variant={sel.iva_aliquota === "10" ? "default" : "outline"} className={sel.iva_aliquota === "10" ? "bg-brand" : ""} onClick={() => updateField(sel.id, { iva_aliquota: "10" })}>IVA 10%</Button>
                    <Button size="sm" variant={sel.iva_aliquota === "22" ? "default" : "outline"} className={sel.iva_aliquota === "22" ? "bg-brand" : ""} onClick={() => updateField(sel.id, { iva_aliquota: "22" })}>IVA 22%</Button>
                  </div>
                )}
                {sel.tipo === "ritenuta" && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant={sel.ritenuta === "NO" ? "default" : "outline"} className={`w-full ${sel.ritenuta === "NO" ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { ritenuta: "NO" })}>No Ritenuta Acconto</Button>
                    <Button size="sm" variant={sel.ritenuta === "SI" ? "default" : "outline"} className={`w-full ${sel.ritenuta === "SI" ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { ritenuta: "SI" })}>Si Ritenuta Acconto</Button>
                  </div>
                )}
                {sel.tipo === "spesa" && (
                  <div className="space-y-1">
                    <Button size="sm" variant={sel.spesa === "si" ? "default" : "outline"} className={`w-full ${sel.spesa === "si" ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { spesa: "si" })}>Spesa fiscalmente detraibile (Bonif. parlante)</Button>
                    <Button size="sm" variant={sel.spesa === "no" ? "default" : "outline"} className={`w-full ${sel.spesa === "no" ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { spesa: "no" })}>Spesa non fisc. detraib. (Bon. Ordinario)</Button>
                  </div>
                )}
                {FREE_TEXT.includes(sel.tipo) && (
                  <Textarea rows={sel.tipo === "fatturazione" || sel.tipo === "dati_condominio" || sel.tipo === "danneggiati" ? 5 : 2} value={sel.testo || ""} onChange={(e) => updateField(sel.id, { testo: e.target.value })} data-testid="conf-text" placeholder="Scrivi qui il testo del campo..." />
                )}

                {sel.tipo !== "firma" && sel.tipo !== "croce" && sel.tipo !== "spunta" ? (
                  <>
                    <div>
                      <Label className="text-xs text-slate-500">Dimensione testo</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateField(sel.id, { font_frac: Math.max(0.008, +(sel.font_frac - 0.002).toFixed(3)) })}><Minus className="w-4 h-4" /></Button>
                        <span className="text-sm w-10 text-center">{Math.round(sel.font_frac * 1000)}</span>
                        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateField(sel.id, { font_frac: Math.min(0.06, +(sel.font_frac + 0.002).toFixed(3)) })}><Plus className="w-4 h-4" /></Button>
                        <Button size="icon" variant={sel.bold ? "default" : "outline"} className={`h-8 w-8 ml-1 ${sel.bold ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { bold: !sel.bold })}><Bold className="w-4 h-4" /></Button>
                        <div className="flex gap-1 ml-auto">
                          {[[0, AlignLeft], [1, AlignCenter], [2, AlignRight]].map(([a, Ic]) => <Button key={a} size="icon" variant={sel.align === a ? "default" : "outline"} className={`h-8 w-8 ${sel.align === a ? "bg-brand" : ""}`} onClick={() => updateField(sel.id, { align: a })}><Ic className="w-4 h-4" /></Button>)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Larghezza campo</Label>
                      <input type="range" min="10" max="90" value={Math.round(sel.w * 100)} onChange={(e) => updateField(sel.id, { w: +e.target.value / 100 })} className="w-full accent-brand" />
                    </div>
                  </>
                ) : (sel.tipo === "croce" || sel.tipo === "spunta") ? (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">Trascina il campo per posizionarlo, regola la dimensione:</p>
                    <div>
                      <Label className="text-xs text-slate-500">Dimensione</Label>
                      <input type="range" min="2" max="15" step="1" value={Math.round(sel.w * 100)}
                        onChange={(e) => updateField(sel.id, { w: +e.target.value / 100, h: +e.target.value / 100 * 0.75 })}
                        className="w-full accent-brand" data-testid="conf-symbol-size" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Larghezza</Label>
                    <input type="range" min="8" max="60" value={Math.round(sel.w * 100)} onChange={(e) => updateField(sel.id, { w: +e.target.value / 100 })} className="w-full accent-brand" />
                    <Label className="text-xs text-slate-500">Altezza</Label>
                    <input type="range" min="4" max="40" value={Math.round(sel.h * 100)} onChange={(e) => updateField(sel.id, { h: +e.target.value / 100 })} className="w-full accent-brand" />
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Pagina</Label>
                  <select value={sel.page} onChange={(e) => updateField(sel.id, { page: +e.target.value })} className="w-full h-9 border rounded px-2 mt-1">
                    {pages.map((p) => <option key={p.page} value={p.page}>Pagina {p.page + 1}</option>)}
                  </select>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
